import React from 'react';

interface StarRatingProps {
    rating: number;
    iconSize?: string;
    color?: string;
    classAdded?: string;
}

const StarRating: React.FC<StarRatingProps> = ({
    rating = 0,
    iconSize = 'h-3.5',
    color = '#F79009',
    classAdded = '',
}) => {
    const validRating = Math.min(Math.max(Number(rating) || 0, 0), 5);
    const fullStars = Math.floor(validRating);
    const hasHalfStar = validRating % 1 >= 0.1;
    const emptyStars = Math.max(0, 5 - fullStars - (hasHalfStar ? 1 : 0));

    const StarFull = () => (
        <svg
            className={iconSize}
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M7 10.1792L4.34583 11.7833C4.23888 11.8514 4.13194 11.883 4.025 11.8781C3.91805 11.8733 3.82569 11.8368 3.74791 11.7687C3.67013 11.7104 3.60937 11.6351 3.56562 11.5427C3.52187 11.4503 3.51458 11.341 3.54375 11.2146L4.24375 8.18125L1.89583 6.125C1.80833 6.05694 1.75243 5.97187 1.72812 5.86979C1.70381 5.76771 1.70624 5.66806 1.73541 5.57083C1.76458 5.47361 1.82048 5.39097 1.90312 5.32292C1.98576 5.25486 2.08541 5.21597 2.20208 5.20625L5.30833 4.94375L6.50416 2.07083C6.55277 1.96389 6.62326 1.88368 6.71562 1.83021C6.80798 1.77674 6.90277 1.75 7 1.75C7.09722 1.75 7.19201 1.77674 7.28437 1.83021C7.37673 1.88368 7.44722 1.96389 7.49583 2.07083L8.70625 4.94375L11.7979 5.20625C11.9146 5.21597 12.0142 5.25486 12.0969 5.32292C12.1795 5.39097 12.2354 5.47361 12.2646 5.57083C12.2937 5.66806 12.2962 5.76771 12.2719 5.86979C12.2476 5.97187 12.1917 6.05694 12.1042 6.125L9.75625 8.18125L10.4562 11.2146C10.4854 11.341 10.4781 11.4503 10.4344 11.5427C10.3906 11.6351 10.3299 11.7104 10.2521 11.7687C10.1743 11.8368 10.0819 11.8733 9.975 11.8781C9.86805 11.883 9.76597 11.8514 9.66875 11.7833L7 10.1792Z"
                fill={color}
            />
        </svg>
    );

    const StarHalf = () => (
        <svg
            className={iconSize}
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M7.99999 4.16667V10.2167L10.4667 11.7L9.79999 8.93333L11.9667 7.05L9.11666 6.8L7.99999 4.16667ZM7.99999 11.6333L4.96666 13.4667C4.84444 13.5444 4.72222 13.5806 4.59999 13.575C4.47777 13.5694 4.37222 13.5278 4.28333 13.45C4.19444 13.3833 4.12499 13.2972 4.07499 13.1917C4.02499 13.0861 4.01666 12.9611 4.04999 12.8167L4.84999 9.35L2.16666 7C2.06666 6.92222 2.00277 6.825 1.97499 6.70833C1.94722 6.59167 1.94999 6.47778 1.98333 6.36667C2.01666 6.25556 2.08055 6.16111 2.17499 6.08333C2.26944 6.00556 2.38333 5.96111 2.51666 5.95L6.06666 5.65L7.43333 2.36667C7.48888 2.24444 7.56944 2.15278 7.67499 2.09167C7.78055 2.03056 7.88888 2 7.99999 2C8.11111 2 8.21944 2.03056 8.32499 2.09167C8.43055 2.15278 8.51111 2.24444 8.56666 2.36667L9.94999 5.65L13.4833 5.95C13.6167 5.96111 13.7305 6.00556 13.825 6.08333C13.9194 6.16111 13.9833 6.25556 14.0167 6.36667C14.05 6.47778 14.0528 6.59167 14.025 6.70833C13.9972 6.825 13.9333 6.92222 13.8333 7L11.15 9.35L11.95 12.8167C11.9833 12.9611 11.975 13.0861 11.925 13.1917C11.875 13.2972 11.8055 13.3833 11.7167 13.45C11.6278 13.5278 11.5222 13.5694 11.4 13.575C11.2778 13.5806 11.1611 13.5444 11.05 13.4667L7.99999 11.6333Z"
                fill={color}
            />
        </svg>
    );

    const StarEmpty = () => (
        <svg
            className={iconSize}
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M4.85625 10.2229L7 8.93958L9.15833 10.2375L8.575 7.81667L10.4708 6.16875L7.97708 5.95L7 3.64583L6.0375 5.93542L3.54375 6.15417L5.43958 7.7875L4.85625 10.2229ZM7 10.1792L4.34583 11.7833C4.23888 11.8514 4.13194 11.883 4.025 11.8781C3.91805 11.8733 3.82569 11.8368 3.74791 11.7687C3.67013 11.7104 3.60937 11.6351 3.56562 11.5427C3.52187 11.4503 3.51458 11.341 3.54375 11.2146L4.24375 8.18125L1.89583 6.125C1.80833 6.05694 1.75243 5.97187 1.72812 5.86979C1.70381 5.76771 1.70624 5.66806 1.73541 5.57083C1.76458 5.47361 1.82048 5.39097 1.90312 5.32292C1.98576 5.25486 2.08541 5.21597 2.20208 5.20625L5.30833 4.94375L6.50416 2.07083C6.55277 1.96389 6.62326 1.88368 6.71562 1.83021C6.80798 1.77674 6.90277 1.75 7 1.75C7.09722 1.75 7.19201 1.77674 7.28437 1.83021C7.37673 1.88368 7.44722 1.96389 7.49583 2.07083L8.70625 4.94375L11.7979 5.20625C11.9146 5.21597 12.0142 5.25486 12.0969 5.32292C12.1795 5.39097 12.2354 5.47361 12.2646 5.57083C12.2937 5.66806 12.2962 5.76771 12.2719 5.86979C12.2476 5.97187 12.1917 6.05694 12.1042 6.125L9.75625 8.18125L10.4562 11.2146C10.4854 11.341 10.4781 11.4503 10.4344 11.5427C10.3906 11.6351 10.3299 11.7104 10.2521 11.7687C10.1743 11.8368 10.0819 11.8733 9.975 11.8781C9.86805 11.883 9.76597 11.8514 9.66875 11.7833L7 10.1792Z"
                fill={color}
            />
        </svg>
    );

    return (
        <div className={`flex gap-1 ${classAdded}`}>
            {[...Array(fullStars)].map((_, i) => (
                <StarFull key={`full-${i}`} />
            ))}
            {hasHalfStar && <StarHalf key="half" />}
            {[...Array(emptyStars)].map((_, i) => (
                <StarEmpty key={`empty-${i}`} />
            ))}
        </div>
    );
};

export default StarRating;
